import { useCallback, useContext, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import uniqueId from 'transmute/uniqueId';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { propertyValuesCache } from '../../cache';
import { useHttpClient } from '../../client/HttpClientContext';
import { HighlySensitivePropertyContext } from '../../dataSensitivity/HighlySensitivePropertyContext';
import { Metrics } from '../../metrics/Metrics';
import { trackPropertyEditsForObjectUpdate } from '../../utils/tracking';
import { useIdentifierContext } from '../../v2/context/IdentifierContext';
import { useGetAdditionalPropertyValue } from '../../v2/hooks/useGetAdditionalPropertyValue';
import { useGetIsFocused } from '../../v2/hooks/useGetIsFocused';
import { useObjectId } from '../../v2/hooks/useObjectId';
import { useObjectTypeId } from '../../v2/hooks/useObjectTypeId';
import { useProperty } from '../../v2/hooks/useProperty';
import { EDIT_INPUT_MODE } from '../../v2/types/PropertyInputV2Component';
import { extractPropertyValues, extractPropertyValuesFromObjectMap } from '../../v2/utils/extractPropertyValues';
import { canValidateImmediately } from '../../validation/utils/canValidateImmediately';
import { buildRefreshAlert, buildRetryAlert } from '../alerts';
import { AutosaveContext } from '../context/AutosaveContext';
import { getPropertyAutosaveEvent } from '../getPropertyAutosaveEvent';
import { usePropertyEdits } from './usePropertyEdits';
import { useSavingMode } from './useSavingMode';
const getShouldAutosaveOnValidationChange = ({
  property,
  isOpen,
  isFocused
}) => {
  const propertyAutosaveEvent = getPropertyAutosaveEvent(property);
  if (propertyAutosaveEvent === 'close') {
    return !isOpen;
  }
  if (propertyAutosaveEvent === 'blur') {
    return !isFocused || canValidateImmediately(property);
  }
  const __never = propertyAutosaveEvent;
  return false;
};
const buildAutosaveRequest = ({
  sourceId,
  objectTypeId,
  objectId,
  edits,
  formId,
  formNodeId,
  previousProperties
}) => {
  return {
    type: 'PROPERTY',
    sourceId,
    objectTypeId,
    objectId: Number(objectId),
    updates: {
      properties: Object.entries(edits).map(([name, value]) => ({
        name,
        value
      })),
      previousProperties: Object.entries(previousProperties).map(([name, value]) => ({
        name,
        value
      }))
    },
    formMetadata: formId && formNodeId ? {
      formId,
      formNodeId
    } : undefined
  };
};
export const useInputAutosave = ({
  onBlur,
  onFocus,
  inputMode,
  onChange,
  onOpenChange,
  onSave,
  onValidationChange,
  enableAutosave,
  use
}) => {
  const autosaveContext = useContext(AutosaveContext);
  const highlySensitivePropertyContext = useContext(HighlySensitivePropertyContext);
  const httpClient = useHttpClient();
  const objectId = useObjectId();
  const objectTypeId = useObjectTypeId();
  const property = useProperty();
  const propertyAutosaveEvent = getPropertyAutosaveEvent(property);
  const getIsFocused = useGetIsFocused();
  const {
    inputId,
    formId,
    formNodeId
  } = useIdentifierContext();
  const getAdditionalPropertyValue = useGetAdditionalPropertyValue();
  const getAdditionalPropertyValueRef = useRef(getAdditionalPropertyValue);
  const {
    data: savingMode,
    loading: savingModeLoading
  } = useSavingMode({
    skip: !enableAutosave
  });

  // Update the ref on each render to avoid stale closures
  getAdditionalPropertyValueRef.current = getAdditionalPropertyValue;

  // this defaults to true if use === 'table-cell' because in a table cell, the first
  // `onOpenChange` function  does not fire when clicking into a table cell formatter.
  const isOpenRef = useRef(use === 'table-cell');
  const validationRef = useRef(undefined);
  const savingModeRef = useRef(savingMode);
  savingModeRef.current = savingMode;
  const {
    clearEdits,
    getHasPendingEdits,
    getPendingEdits,
    getOriginalValues,
    onChange: wrappedOnChange
  } = usePropertyEdits({
    onChange
  });

  // Use non-wrapped change handler for undo because we do not want to track undo changes as edits
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const registerHandler = autosaveContext === null || autosaveContext === void 0 ? void 0 : autosaveContext.registerHandler;
  useEffect(() => {
    return registerHandler === null || registerHandler === void 0 ? void 0 : registerHandler({
      id: inputId,
      getIsHandlerForRequest: request => request.sourceId === inputId,
      getIsValidRequest: () => {
        var _validationRef$curren;
        return !!((_validationRef$curren = validationRef.current) !== null && _validationRef$curren !== void 0 && _validationRef$curren.saveable);
      }
    });
  }, [inputId, registerHandler]);

  // AutosaveContext tracks global property edit state to prevent some behavior in synchronized UIs,
  // where validation may trigger autosave events while a user is still typing. This can happen
  // if we have two properties in a form, for example.
  const getIsPropertyBeingEdited = useCallback(() => autosaveContext === null || autosaveContext === void 0 ? void 0 : autosaveContext.getIsEditing({
    propertyName: property.name,
    objectTypeId,
    objectId: objectId !== null && objectId !== void 0 ? objectId : ''
  }), [autosaveContext, objectId, objectTypeId, property.name]);
  const setIsPropertyBeingEdited = useCallback(isEditing => {
    // Table cells are a particularly annoying case because they support ENTER keypresses
    // which do not play well with focus-based editing state. We special case this for now,
    // but ideally the Trellis migration will improve our options here.
    if (use !== 'table-cell') {
      autosaveContext === null || autosaveContext === void 0 || autosaveContext.setIsEditing({
        propertyName: property.name,
        objectTypeId,
        objectId: objectId !== null && objectId !== void 0 ? objectId : '',
        isEditing
      });
    }
  }, [autosaveContext, objectId, objectTypeId, property.name, use]);
  const attemptSave = useCallback(() => {
    var _validationRef$curren2;
    // Table cell does not play well with our autosave request handlers because it unmounts the input,
    // so we need to block enqueuing requests when the input is not saveable in a table cell.
    if (use === 'table-cell' && !((_validationRef$curren2 = validationRef.current) !== null && _validationRef$curren2 !== void 0 && _validationRef$curren2.saveable)) {
      return;
    }
    if (!enableAutosave || !autosaveContext || inputMode !== EDIT_INPUT_MODE || !objectId || !getHasPendingEdits()) {
      return;
    }
    const edits = Object.assign({}, getPendingEdits());
    const originalValues = Object.assign({}, getOriginalValues());
    const request = buildAutosaveRequest({
      sourceId: inputId,
      objectTypeId,
      objectId,
      edits,
      previousProperties: originalValues,
      formId,
      formNodeId
    });
    const handleSave = onFailure => {
      autosaveContext.enqueueRequest({
        request,
        onUndo: () => {
          clearEdits();
          onChangeRef.current(originalValues);
        },
        onSuccess: updates => {
          clearEdits();
          const allSavedPropertyValues = extractPropertyValues(updates[objectId]);
          const savedHighlySensitivePropertyValues = extractPropertyValues(updates[objectId], update => update.sensitivityLevel === 'high');
          if (Object.keys(savedHighlySensitivePropertyValues).length > 0) {
            highlySensitivePropertyContext === null || highlySensitivePropertyContext === void 0 || highlySensitivePropertyContext.updateHighlySensitivePropertyValues({
              objectTypeId,
              objectId,
              values: savedHighlySensitivePropertyValues
            });
          }
          trackPropertyEditsForObjectUpdate({
            objectTypeId,
            objectId,
            savedPropertyValues: allSavedPropertyValues,
            getAdditionalPropertyValue: getAdditionalPropertyValueRef.current
          });
          const cacheKey = {
            objectTypeId,
            objectId
          };
          if (propertyValuesCache.has(cacheKey)) {
            // only if the cache is populated, update it with new values passed to onSave.
            propertyValuesCache.get(cacheKey, httpClient).then(currentCachedValues => {
              const updatedValues = Object.assign({}, currentCachedValues, updates[objectId]);
              propertyValuesCache.set(cacheKey, updatedValues);
            }).catch(() => {
              // the cache stores the promise value, so this will throw
              // more often than we want- we can ignore it, as we are already
              // capturing the original error in the cache fetcher.
            });
          }
          onSave(extractPropertyValuesFromObjectMap(updates));
        },
        onClear: () => {
          clearEdits();
          onChangeRef.current(originalValues);
        },
        onFailure
      });
      if (savingModeRef.current === 'auto') {
        autosaveContext.saveValidRequests();
      }
    };
    handleSave(error => {
      const retryAlertId = uniqueId('autosave');
      FloatingAlertStore.addAlert(buildRetryAlert({
        id: retryAlertId,
        __error: error,
        onRetry: () => {
          FloatingAlertStore.removeAlert(retryAlertId);
          handleSave(retryError => {
            FloatingAlertStore.addAlert(buildRefreshAlert({
              __error: retryError
            }));
          });
        }
      }));
    });
  }, [use, enableAutosave, autosaveContext, inputMode, objectId, getHasPendingEdits, getPendingEdits, getOriginalValues, inputId, objectTypeId, formId, formNodeId, clearEdits, highlySensitivePropertyContext, onSave, httpClient]);
  const wrappedOnBlur = useCallback(evt => {
    onBlur(evt);
    if (propertyAutosaveEvent === 'blur') {
      setIsPropertyBeingEdited(false);
      attemptSave();
    }
  }, [attemptSave, onBlur, propertyAutosaveEvent, setIsPropertyBeingEdited]);
  const wrappedOnFocus = useCallback(evt => {
    onFocus(evt);
    if (propertyAutosaveEvent === 'blur') {
      setIsPropertyBeingEdited(true);
    }
  }, [onFocus, propertyAutosaveEvent, setIsPropertyBeingEdited]);
  const wrappedOnOpenChange = useCallback(evt => {
    onOpenChange(evt);
    isOpenRef.current = evt.target.value;
    if (propertyAutosaveEvent === 'close' && !isOpenRef.current) {
      attemptSave();
    }
  }, [onOpenChange, propertyAutosaveEvent, attemptSave]);
  const wrappedOnValidationChange = useCallback(validation => {
    /**
     * React 18's concurrent rendering (via createRoot) batches state updates,
     * which can cause validationRef to be read by other components (e.g.,
     * invalidRequests in useAutosaveCore) before downstream state updates
     * from onValidationChange have propagated. flushSync ensures the ref
     * update and all resulting re-renders complete synchronously.
     *
     * See: https://hubspot.slack.com/archives/C50LV5B97/p1765413862124909
     */
    flushSync(() => {
      validationRef.current = validation;
      onValidationChange(validation);
    });
    const isPropertyBeingEdited = getIsPropertyBeingEdited();
    const shouldAutosaveOnValidationChange = getShouldAutosaveOnValidationChange({
      property,
      isOpen: isOpenRef.current,
      isFocused: getIsFocused()
    });
    if (shouldAutosaveOnValidationChange && !isPropertyBeingEdited) {
      attemptSave();
    }
  }, [onValidationChange, getIsPropertyBeingEdited, property, getIsFocused, attemptSave]);

  // Bundle these into refs to avoid stale closures, while limiting the number of times
  // the cleanup effect is triggered. Some fields are unlikely to change so we do not mind
  // dependeing on them (e.g., formId, use)
  const attemptSaveRef = useRef(attemptSave);
  const autosaveContextRef = useRef(autosaveContext);
  const getOriginalValuesRef = useRef(getOriginalValues);
  getOriginalValuesRef.current = getOriginalValues;
  attemptSaveRef.current = attemptSave;
  autosaveContextRef.current = autosaveContext;
  useEffect(() => () => {
    // To ensure table cells save when they unmount our input and replace it with a FormattedPropertyV2,
    // make a last attempt to save
    if (enableAutosave && use === 'table-cell') {
      attemptSaveRef.current();
    } else {
      var _autosaveContextRef$c;
      // Clear any requests and revert to original value to ensure that host app is notified
      // and can update any other inputs on the page. This can notably occur if a user changes a
      // controlling property and an invalid dependent field change gets removed from the form.
      if ((_autosaveContextRef$c = autosaveContextRef.current) !== null && _autosaveContextRef$c !== void 0 && _autosaveContextRef$c.hasRequestForSource(inputId)) {
        var _autosaveContextRef$c2;
        onChangeRef.current(getOriginalValuesRef.current());
        (_autosaveContextRef$c2 = autosaveContextRef.current) === null || _autosaveContextRef$c2 === void 0 || _autosaveContextRef$c2.cancelRequestsForSource(inputId);
      }
    }
  }, [use, enableAutosave, inputId, property.name, objectId, objectTypeId]);
  useEffect(() => {
    if (!savingModeLoading && savingModeRef.current === 'auto') {
      var _autosaveContextRef$c3;
      (_autosaveContextRef$c3 = autosaveContextRef.current) === null || _autosaveContextRef$c3 === void 0 || _autosaveContextRef$c3.saveValidRequests();
    }
    // We explicitly only want to try this once when saving mode finishes loading
    // to kick off saving any pending changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingModeLoading]);
  const handleBeforeUnload = useCallback(e => {
    if (enableAutosave && getHasPendingEdits() && objectId) {
      Metrics.counter('autosave-pending-edits-and-beforeunload').increment();
      e.preventDefault();
      e.returnValue = true;
    }
  }, [enableAutosave, getHasPendingEdits, objectId]);
  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);
  return {
    onBlur: wrappedOnBlur,
    onFocus: wrappedOnFocus,
    onChange: wrappedOnChange,
    onOpenChange: wrappedOnOpenChange,
    onValidationChange: wrappedOnValidationChange,
    attemptAutosave: attemptSave
  };
};